import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import {
  listDocumentsApi, documentCategoriesApi, uploadDocumentApi,
  deleteDocumentApi, documentDownloadUrlApi, updateDocumentApi,
  updateDocumentCategoriesApi, listCompanyUsers,
} from "../api";
import { normalizeApiError } from "../lib/apiError";
import AppPage from "../components/AppPage";

const CATEGORY_LABELS = {
  contract: "Договор",
  order: "Приказ",
  application: "Заявление",
  certificate: "Справка",
  other: "Прочее",
};

function catLabel(c) {
  return CATEGORY_LABELS[c] || c;
}

function formatSize(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} Б`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} КБ`;
  return `${(b / 1024 / 1024).toFixed(1)} МБ`;
}

function iconFor(mime) {
  if (mime?.includes("pdf")) return "bi-file-earmark-pdf-fill text-danger";
  if (mime?.includes("image")) return "bi-file-earmark-image-fill text-info";
  if (mime?.includes("word")) return "bi-file-earmark-word-fill text-primary";
  return "bi-file-earmark-fill text-secondary";
}

export default function Documents() {
  const { user } = useAuth();
  const isAdmin = user?.role === "director" || user?.role === "hr";
  const [docs, setDocs] = useState([]);
  const [users, setUsers] = useState([]);
  const [cats, setCats] = useState([]);
  const [filterCat, setFilterCat] = useState("");
  const [filterUser, setFilterUser] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [showCats, setShowCats] = useState(false);

  // Load categories + users once
  useEffect(() => {
    documentCategoriesApi().then(r => setCats(r.categories || [])).catch(() => {});
    if (isAdmin) listCompanyUsers().then(rows => setUsers(rows || [])).catch(() => {});
  }, [isAdmin]);

  // Load documents on filter change or manual trigger
  const [reloadCounter, setReloadCounter] = useState(0);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const params = {};
        if (filterCat) params.category = filterCat;
        if (filterUser) params.user_id = filterUser;
        const list = await listDocumentsApi(params);
        if (!cancelled) { setDocs(list); setErr(""); }
      } catch (e) {
        if (!cancelled) setErr(normalizeApiError(e));
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filterCat, filterUser, reloadCounter]);

  const reload = () => setReloadCounter(n => n + 1);

  const onDownload = async (doc) => {
    try {
      const { url } = await documentDownloadUrlApi(doc.doc_id);
      window.open(url, "_blank");
    } catch (e) { alert(normalizeApiError(e)); }
  };

  const onDelete = async (doc) => {
    if (!window.confirm(`Удалить «${doc.title}»?`)) return;
    try { await deleteDocumentApi(doc.doc_id); reload(); }
    catch (e) { alert(normalizeApiError(e)); }
  };

  const onToggleVisibility = async (doc) => {
    try {
      await updateDocumentApi(doc.doc_id, { visible_to_employee: !doc.visible_to_employee });
      reload();
    } catch (e) { alert(normalizeApiError(e)); }
  };

  return (
    <AppPage testId="documents-page" bodyScroll="scroll">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <h3 className="mb-0 me-auto"><i className="bi bi-folder2-open me-2"></i>Документы</h3>
        {isAdmin && (
          <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowCats(true)}
                  data-testid="docs-edit-categories">
            <i className="bi bi-tags me-1"></i>Категории
          </button>
        )}
        <button className="btn btn-primary btn-sm" onClick={() => setShowUpload(true)}
                data-testid="docs-upload-btn">
          <i className="bi bi-upload me-1"></i>Загрузить
        </button>
      </div>

      {err && <div className="alert alert-danger py-2 small">{err}</div>}

      <div className="d-flex flex-wrap gap-2 mb-3">
        <select className="form-select form-select-sm" style={{ width: "auto" }}
                value={filterCat} onChange={e => setFilterCat(e.target.value)}
                data-testid="docs-filter-category">
          <option value="">Все категории</option>
          {cats.map(c => <option key={c} value={c}>{catLabel(c)}</option>)}
        </select>
        {isAdmin && (
          <select className="form-select form-select-sm" style={{ width: "auto" }}
                  value={filterUser} onChange={e => setFilterUser(e.target.value)}
                  data-testid="docs-filter-user">
            <option value="">Все сотрудники</option>
            <option value="shared">Общие документы компании</option>
            {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
          </select>
        )}
      </div>

      {busy && <div className="text-center py-3"><span className="spinner-border spinner-border-sm" /></div>}

      {!busy && docs.length === 0 && (
        <div className="text-center text-muted py-5">
          <i className="bi bi-folder-x" style={{ fontSize: 48, opacity: 0.4 }}></i>
          <p className="mt-3 mb-0">Документов пока нет</p>
        </div>
      )}

      {docs.length > 0 && (
        <div className="table-responsive">
          <table className="table table-sm align-middle" data-testid="docs-table">
            <thead><tr>
              <th></th>
              <th>Название</th>
              <th>Категория</th>
              {isAdmin && <th>Сотрудник</th>}
              <th>Размер</th>
              <th>Загружен</th>
              <th></th>
            </tr></thead>
            <tbody>
              {docs.map(d => (
                <tr key={d.doc_id} data-testid={`docs-row-${d.doc_id}`}>
                  <td><i className={`bi ${iconFor(d.mime_type)} fs-4`}></i></td>
                  <td>
                    <button onClick={() => onDownload(d)} className="btn btn-link p-0 text-decoration-none">
                      {d.title}
                    </button>
                    <div className="small text-muted">{d.file_name}</div>
                  </td>
                  <td><span className="badge bg-secondary">{catLabel(d.category)}</span></td>
                  {isAdmin && (
                    <td className="small">
                      {d.user_id
                        ? (users.find(u => u.user_id === d.user_id)?.name || d.user_id)
                        : <em>Общий</em>}
                    </td>
                  )}
                  <td className="small">{formatSize(d.size_bytes)}</td>
                  <td className="small">{new Date(d.uploaded_at).toLocaleDateString("ru-RU")}</td>
                  <td className="text-end">
                    <div className="btn-group btn-group-sm">
                      <button className="btn btn-outline-primary" onClick={() => onDownload(d)} title="Скачать">
                        <i className="bi bi-download"></i>
                      </button>
                      {isAdmin && (
                        <button className="btn btn-outline-secondary" onClick={() => onToggleVisibility(d)}
                                title={d.visible_to_employee ? "Скрыть от сотрудника" : "Показать сотруднику"}>
                          <i className={`bi bi-${d.visible_to_employee ? "eye" : "eye-slash"}`}></i>
                        </button>
                      )}
                      {(isAdmin || d.uploaded_by === user?.user_id) && (
                        <button className="btn btn-outline-danger" onClick={() => onDelete(d)} title="Удалить">
                          <i className="bi bi-trash"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showUpload && (
        <UploadModal cats={cats} users={users} isAdmin={isAdmin}
                     onClose={() => setShowUpload(false)}
                     onDone={() => { setShowUpload(false); reload(); }} />
      )}
      {showCats && (
        <CategoriesModal cats={cats} onClose={() => setShowCats(false)}
                         onDone={(newCats) => { setCats(newCats); setShowCats(false); }} />
      )}
    </AppPage>
  );
}

function UploadModal({ cats, users, isAdmin, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(cats[0] || "other");
  const [userId, setUserId] = useState("");
  const [visible, setVisible] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setErr("Выберите файл"); return; }
    if (!title.trim()) { setErr("Введите название"); return; }
    setBusy(true); setErr("");
    try {
      await uploadDocumentApi(file, {
        title: title.trim(), category,
        user_id: userId || undefined,
        visible_to_employee: visible,
      });
      onDone();
    } catch (ex) { setErr(normalizeApiError(ex)); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="modal-dialog" onClick={e => e.stopPropagation()}>
        <form className="modal-content" onSubmit={submit} data-testid="upload-modal">
          <div className="modal-header">
            <h5 className="modal-title">Загрузить документ</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {err && <div className="alert alert-danger py-2 small">{err}</div>}
            <div className="mb-3">
              <label className="form-label small">Файл (PDF, JPG, PNG, DOC, DOCX — до 10 МБ)</label>
              <input type="file" className="form-control" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                     onChange={e => setFile(e.target.files?.[0] || null)}
                     data-testid="upload-file" required />
            </div>
            <div className="mb-3">
              <label className="form-label small">Название</label>
              <input type="text" className="form-control" value={title} onChange={e => setTitle(e.target.value)}
                     placeholder="Например, Договор найма Иванова" data-testid="upload-title" required />
            </div>
            <div className="mb-3">
              <label className="form-label small">Категория</label>
              <select className="form-select" value={category} onChange={e => setCategory(e.target.value)}
                      data-testid="upload-category">
                {cats.map(c => <option key={c} value={c}>{catLabel(c)}</option>)}
              </select>
            </div>
            {isAdmin && (
              <>
                <div className="mb-3">
                  <label className="form-label small">Кому относится</label>
                  <select className="form-select" value={userId} onChange={e => setUserId(e.target.value)}
                          data-testid="upload-user">
                    <option value="">Себе</option>
                    <option value="shared">Общий документ компании</option>
                    {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
                  </select>
                </div>
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="visEmp"
                         checked={visible} onChange={e => setVisible(e.target.checked)}
                         data-testid="upload-visible" />
                  <label className="form-check-label small" htmlFor="visEmp">
                    Сотрудник сможет увидеть и скачать
                  </label>
                </div>
              </>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn btn-primary" disabled={busy} data-testid="upload-submit">
              {busy ? "Загрузка…" : "Загрузить"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CategoriesModal({ cats, onClose, onDone }) {
  const [list, setList] = useState([...cats]);
  const [newCat, setNewCat] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const add = () => {
    const c = newCat.trim();
    if (!c || list.includes(c)) return;
    setList([...list, c]); setNewCat("");
  };
  const remove = (c) => setList(list.filter(x => x !== c));

  const save = async () => {
    if (list.length === 0) { setErr("Должна быть хотя бы одна категория"); return; }
    setBusy(true); setErr("");
    try {
      const r = await updateDocumentCategoriesApi(list);
      onDone(r.categories);
    } catch (e) { setErr(normalizeApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="modal-dialog" onClick={e => e.stopPropagation()}>
        <div className="modal-content" data-testid="categories-modal">
          <div className="modal-header">
            <h5 className="modal-title">Категории документов</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {err && <div className="alert alert-danger py-2 small">{err}</div>}
            <p className="small text-muted">5 категорий по умолчанию: договор, приказ, заявление, справка, прочее. Можете добавить свои или удалить ненужные.</p>
            <ul className="list-group mb-3">
              {list.map(c => (
                <li key={c} className="list-group-item d-flex justify-content-between align-items-center py-2">
                  <span>{catLabel(c)} <small className="text-muted">({c})</small></span>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => remove(c)}>
                    <i className="bi bi-x"></i>
                  </button>
                </li>
              ))}
            </ul>
            <div className="input-group">
              <input type="text" className="form-control" value={newCat}
                     onChange={e => setNewCat(e.target.value)}
                     onKeyDown={e => e.key === "Enter" && (e.preventDefault(), add())}
                     placeholder="Новая категория (напр. NDA, оффер)" />
              <button className="btn btn-outline-primary" type="button" onClick={add}>Добавить</button>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Отмена</button>
            <button type="button" className="btn btn-primary" onClick={save} disabled={busy}>
              {busy ? "Сохранение…" : "Сохранить"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
